import {useContext, useEffect, useState} from 'react'
import {PaymentCalenderPageContext} from '../../contexts'
import {SectionForm} from '../../../../components/SectionForm'
import {useForm} from 'react-hook-form'
import {
  STORE_FILTER_OPTIONS,
  handleCalendarMonthChange,
  handleCalendarViewChange,
  makeSemicolonSerratedValues,
} from '../../utils'
import {format} from 'date-fns'
import useGetJobData from '../../../jobs/hooks/useGetJobData'
import usePermission from '../../../../hook/usePermission'
import moment from 'moment'

const PaymentCalenderHeader = () => {
  const {
    filters,
    suppliersList,
    isLoadingSuppliers,
    calendarRef,
    updateCalendarMonth,
    onApplyCalendarFilters,
    resetCalendarFilters,
    currentView,
    setCurrentView,
    onApplyPaymentListFilters,
    resetPaymentListFilters,
  } = useContext(PaymentCalenderPageContext)

  const [currentCalendarView, setCurrentCalendarView] = useState<'dayGridMonth' | 'dayGridWeek'>(
    'dayGridMonth'
  )
  const [viewDateRange, setViewDateRange] = useState(format(new Date(), 'MMMM yyyy'))
  const [isCurrentDateIsInView, setIsCurrentDateIsInView] = useState(true)

  const {hasPermission} = usePermission()
  const {jobData, runTask, refetchJobData} = useGetJobData('update_payment_processes_data')

  useEffect(() => {
    let intervalId: any = null
    if (jobData?.status === 'running') {
      intervalId = setInterval(() => {
        refetchJobData()
      }, 30000)
    }
    return () => {
      if (intervalId) {
        clearInterval(intervalId)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jobData?.status === 'running', refetchJobData])

  const {
    register,
    control,
    formState: {errors, isDirty},
    handleSubmit,
    reset,
    watch,
  } = useForm<any>({
    defaultValues: {
      store: '',
      suppliers: [],
    },
  })

  const watchedValues: any = watch()

  const isFilterApplied = filters.store_filter !== '' || filters.supplier_filter?.length > 0

  const submitFilters = (data: any) => {
    const filters = {
      store: data.store,
      suppliers: makeSemicolonSerratedValues(data.suppliers) || [],
    }

    // Apply filters based on current view
    onApplyPaymentListFilters(filters)
    onApplyCalendarFilters(filters)
  }

  const resetFilters = () => {
    reset()
    resetPaymentListFilters()
    resetCalendarFilters()
  }

  const onCalendarMonthChange = (action: 'prev' | 'next' | 'today') => {
    handleCalendarMonthChange(
      action,
      calendarRef,
      setViewDateRange,
      setIsCurrentDateIsInView,
      updateCalendarMonth
    )
  }

  const handleChangeView = (view: 'list' | 'calendar') => {
    updateCalendarMonth({
      start: format(moment().startOf('month').toDate(), 'yyyy-MM-dd'),
      end: format(moment().endOf('month').toDate(), 'yyyy-MM-dd'),
    })
    setViewDateRange(format(new Date(), 'MMMM yyyy'))
    setCurrentView(view)
    setIsCurrentDateIsInView(true)
  }

  return (
    <div className='border-0 pt-6 w-100 mb-5'>
      <div>
        {/* Filters Section */}
        <div className='d-flex align-items-center justify-content-between'>
          <div className='d-flex gap-3 align-items-center mb-4'>
            <SectionForm
              id='payment-filters'
              inputSectionClass='d-flex align-items-center gap-3'
              register={register as any}
              control={control}
              errors={errors}
              inputs={[
                {
                  id: 'store-filter',
                  registerKey: 'store',
                  inputClass: 'w-175px',
                  isSelect: true,
                  label: 'Store',
                  placeholder: 'Both Stores',
                  options: STORE_FILTER_OPTIONS,
                  labelKey: 'label',
                  valueKey: 'value',
                  isNullable: false,
                },
                {
                  id: 'supplier-filter',
                  registerKey: 'suppliers',
                  inputClass: 'w-200px',
                  isMultiSelect: true,
                  label: 'Suppliers',
                  placeholder: 'Select Suppliers',
                  options: suppliersList,
                  labelKey: 'label',
                  valueKey: 'value',
                  isLoading: isLoadingSuppliers,
                  hasSelectAll: true,
                  ...(!watchedValues?.suppliers?.length && {selectedValues: []}),
                },
              ]}
            />

            {/* Filter Buttons */}
            <div className='d-flex gap-2 mt-8'>
              <button
                id='apply_filter'
                className='btn btn-primary'
                onClick={() => handleSubmit(submitFilters)()}
                disabled={isLoadingSuppliers || !isDirty}
              >
                Apply Filters
              </button>
              <button
                id='reset_filters'
                className='btn btn-outline'
                onClick={resetFilters}
                disabled={!isFilterApplied}
              >
                Reset Filters
              </button>
            </div>
          </div>
          {/* Sync Button */}
          {hasPermission('analytics_payment schedule', 'write') && (
            <div className='position-relative d-flex align-items-center'>
              <button
                onClick={() => runTask('update_payment_processes_data')}
                className='btn btn-outline'
              >
                <div className='d-flex align-items-center'>
                  {jobData?.status === 'running' ? (
                    <span className='spinner-border spinner-border-sm align-middle me-2'></span>
                  ) : (
                    <i className='fas fa-sync cursor-pointer fs-5 me-2'></i>
                  )}
                  {jobData?.status === 'running' ? 'Syncing...' : 'Sync Data'}
                </div>
              </button>
            </div>
          )}
        </div>

        {/* Calendar Navigation Section */}
        <div className='d-flex align-items-center justify-content-between mt-5'>
          <div className='d-flex align-items-center gap-5'>
            {/* List/Calendar View Toggle */}
            <div className='bg-light rounded-2 p-1 border border-1'>
              <button
                className={`btn ${currentView === 'list' ? 'bg-white' : ''}`}
                onClick={() => handleChangeView('list')}
              >
                <i className='fa fa-list me-2'></i>
                List
              </button>
              <button
                className={`btn ${currentView === 'calendar' ? 'bg-white' : ''}`}
                onClick={() => handleChangeView('calendar')}
              >
                <i className='fa fa-calendar me-2'></i>
                Calendar
              </button>
            </div>
            {/* Calendar Navigation - Hide in list view */}
            {currentView === 'calendar' && (
              <div className='d-flex align-items-center gap-2'>
                <button
                  type='button'
                  onClick={() => onCalendarMonthChange('prev')}
                  className='btn btn-light'
                >
                  <i className='fas fa-chevron-left fs-4'></i>
                </button>

                <button
                  type='button'
                  onClick={() => onCalendarMonthChange('next')}
                  className='btn btn-light'
                >
                  <i className='fas fa-chevron-right fs-4'></i>
                </button>

                {!isCurrentDateIsInView && (
                  <button
                    type='button'
                    className='btn btn-light'
                    onClick={() => onCalendarMonthChange('today')}
                  >
                    Today
                  </button>
                )}
              </div>
            )}
          </div>

          {/* Current Month Display - Hide in list view */}
          {currentView === 'calendar' && <h4 className='mb-0 mx-4'>{viewDateRange}</h4>}

          {/* Calendar View Toggle and Sync Button */}
          <div className='d-flex align-items-center gap-3'>
            {/* Calendar View Toggle - Hide in list view */}
            {currentView === 'calendar' && (
              <div className='bg-light rounded-2 p-1 border border-1'>
                <button
                  className={`btn ${currentCalendarView === 'dayGridMonth' ? 'bg-white' : ''}`}
                  onClick={() =>
                    handleCalendarViewChange('dayGridMonth', calendarRef, setCurrentCalendarView)
                  }
                >
                  <i className='fa fa-list me-2'></i>
                  Month
                </button>
                <button
                  className={`btn ${currentCalendarView === 'dayGridWeek' ? 'bg-white' : ''}`}
                  onClick={() =>
                    handleCalendarViewChange('dayGridWeek', calendarRef, setCurrentCalendarView)
                  }
                >
                  <i className='fa fa-calendar me-2'></i>
                  Week
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

export default PaymentCalenderHeader
